import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Router, provideRouter } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { RECAPTCHA_LOADER_OPTIONS, RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { lastValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { SlowConnectionInterceptor } from './core/interceptors/slow-connection.interceptor';
import { TokenStorageService } from './core/services/auth/tokens/token-storage.service';
import { AcademicClassEffects } from './core/store/academics/academic.effects';
import { academicClassReducer } from './core/store/academics/academic.reducer';
import { AccessEffects } from './core/store/access/access.effects';
import { accessReducer } from './core/store/access/access.reducer';
import { ActivityEffects } from './core/store/activities/activity.effects';
import { activityReducer } from './core/store/activities/activity.reducer';
import { AppState } from './core/store/app.state';
import { EventEffects } from './core/store/events/event.effects';
import { eventReducer } from './core/store/events/event.reducer';
import { FinanceEffects } from './core/store/finance/finance.effects';
import { financeReducer } from './core/store/finance/finance.reducer';
import { GuardianEffects } from './core/store/guardian/guardian.effects';
import { guardianReducer } from './core/store/guardian/guardian.reducer';
import { ImageCacheEffects } from './core/store/image-cache/image-cache.effects';
import { NotificationEffects } from './core/store/notifications/notification.effects';
import { notificationReducer } from './core/store/notifications/notification.reducer';
import { ResourceEffects } from './core/store/resources/resource.effects';
import { libraryReducer, resourceReducer } from './core/store/resources/resource.reducer';
import { RouterEffects } from './core/store/router/router.effects';
import { SchoolEffects } from './core/store/schools/school.effects';
import { schoolReducer } from './core/store/schools/school.reducer';
import { StaffEffects } from './core/store/staff/staff.effects';
import { staffReducer } from './core/store/staff/staff.reducer';
import { StudentEffects } from './core/store/students/student.effects';
import { studentReducer } from './core/store/students/student.reducer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimationsAsync(),
    provideEnvironmentNgxMask(),
    provideHttpClient(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideStore<AppState>({
      router: routerReducer,
      guardian: guardianReducer,
      students: studentReducer,
      events: eventReducer,
      schools: schoolReducer,
      resources: resourceReducer,
      academics: academicClassReducer,
      staff: staffReducer,
      notifications: notificationReducer,
      activities: activityReducer,
      finance: financeReducer,
      access: accessReducer,
      library: libraryReducer,
    }),

    provideEffects([
      ImageCacheEffects,
      GuardianEffects,
      StudentEffects,
      EventEffects,
      SchoolEffects,
      ResourceEffects,
      AcademicClassEffects,
      StaffEffects,
      NotificationEffects,
      RouterEffects,
      ActivityEffects,
      FinanceEffects,
      AccessEffects,
    ]),

    provideRouterStore(),

    ...(isDevMode()
      ? [
          provideStoreDevtools({
            maxAge: 25,
            autoPause: true,
            trace: false,
            traceLimit: 75,
            connectInZone: true,
          }),
        ]
      : []),

    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const tokenService = inject(TokenStorageService);
      const apiBaseUrl = environment.apiBaseUrl;

      // Auth link - adds Bearer token.
      const authLink = setContext(async () => {
        const token = await lastValueFrom(tokenService.getToken());
        if (!token) return {};
        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      });

      const operationNameLink = new ApolloLink((operation, forward) => {
        operation.setContext(({ uri, ...rest }: any) => {
          const baseUri = uri || apiBaseUrl + '/data/graphql';
          const separator = baseUri.includes('?') ? '&' : '?';
          return {
            ...rest,
            uri: `${baseUri}${separator}${encodeURIComponent(operation.operationName)}`,
          };
        });
        return forward(operation);
      });

      return {
        link: ApolloLink.from([authLink, operationNameLink, httpLink.create({ uri: apiBaseUrl + '/data/graphql' })]),
        cache: new InMemoryCache(),
      };
    }),

    importProvidersFrom(HttpClientModule, RecaptchaV3Module),

    provideIonicAngular({}),

    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.security.recaptchaSiteKey,
    },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useValue: {
        onBeforeLoad: (url: URL) => {
          url.searchParams.set('render', environment.security.recaptchaSiteKey);
          return { url };
        },
      },
    },

    // Registered before AuthInterceptor so it wraps the full request lifecycle
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SlowConnectionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
  ],
};
